import { Button, SpaceBetween } from '@cloudscape-design/components';
import { useState } from 'react';
import { useApi } from 'src/hooks/useApi';
import { useTranslate } from 'src/i18n/useTranslate';
import './TopicRating.scss';

interface TopicRatingProps {
    artifactId: string;
    artifactTitle: string;
    topicId: string;
    topicTitle: string;
}

export default function TopicRating({
    artifactId,
    artifactTitle,
    topicId,
    topicTitle,
}: TopicRatingProps) {
    const t = useTranslate();
    const [expanded, setExpanded] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    const { makeRequest } = useApi({ method: 'POST', url: '/rating' });

    const handleRate = (value: number) => {
        const newRating = rating === value ? 0 : value;
        setRating(newRating);
        if (newRating > 0) {
            setSubmitted(true);
            setExpanded(false);
            makeRequest({
                data: {
                    rating: newRating,
                    artifactId,
                    topicId,
                    pageUrl: window.location.href,
                },
            });
        } else {
            setSubmitted(false);
        }
    };

    if (!expanded) {
        return (
            <div
                data-topic-rating
                style={{
                    transformOrigin: 'center center',
                    display: 'inline-block',
                }}
            >
                <Button
                    variant="normal"
                    iconName={submitted ? 'star-filled' : 'star'}
                    ariaLabel={
                        submitted
                            ? t('rated', `Rated ${rating}/5`)
                            : t('rate_topic', 'Rate this topic')
                    }
                    onClick={() => setExpanded(true)}
                    data-testid="topic-rating-toggle"
                    data-topic-rating-button
                >
                    {submitted
                        ? t('your_rating', `Rated ${rating}/5`)
                        : t('rate_topic', 'Rate this topic')}
                </Button>
            </div>
        );
    }

    return (
        <div
            data-topic-rating
            data-topic-rating-expanded
            style={{
                transformOrigin: 'center center',
                display: 'inline-block',
            }}
        >
            <SpaceBetween size="xxxs" direction="horizontal">
                {[1, 2, 3, 4, 5].map((n) => (
                    <div
                        key={n}
                        onMouseEnter={() => setHoveredStar(n)}
                        onMouseLeave={() => setHoveredStar(0)}
                    >
                        <Button
                            data-testid={`topic-rating-${n}-star`}
                            data-topic-rating-star={
                                rating >= n
                                    ? 'filled'
                                    : hoveredStar >= n
                                    ? 'hovered'
                                    : 'empty'
                            }
                            iconName={
                                rating >= n || hoveredStar >= n
                                    ? 'star-filled'
                                    : 'star'
                            }
                            variant="icon"
                            iconAlt={`${n} star`}
                            onClick={() => handleRate(n)}
                        />
                    </div>
                ))}
            </SpaceBetween>
        </div>
    );
}
